import React, {
  useEffect,
} from 'react';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  useNotify,
} from 'core/hooks';
import {
  faxEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  AddPatientDialogProps,
} from 'pages/Dashboard/pages/Fax/Overview/AddPatientDialog';
import {
  useUploadInboundFileToPatientChart,
} from 'pages/Dashboard/pages/Fax/hooks';
import {
  useForm,
} from 'react-hook-form';
import FileTypeSelect from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileTypeSelect';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Controller from 'core/components/FormController';

type AddPatientFormData = {
  patient: PatientCompact;
  fileType: string;
};

export default function AddPatientForm({
  open,
  onClose,
  id,
}: AddPatientDialogProps) {
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = useForm<AddPatientFormData>({
    mode: 'onChange',
    defaultValues: {},
  });
  const {
    mutateAsync: uploadInboundFaxToPatientChart,
    isError: isErrorUpload,
    isSuccess: isSuccessUpload,
  } = useUploadInboundFileToPatientChart();
  const notify = useNotify();

  const onSubmit = async (data: AddPatientFormData) => {
    const { patient, fileType } = data;
    await uploadInboundFaxToPatientChart({
      patientId: patient?.patientId ?? 0,
      multimediaType: fileType,
      inboundFaxId: id,
    });
  };

  useEffect(() => {
    if (isErrorUpload) {
      notify({
        variant: 'error',
        message: 'Upload Failed',
      });
    }
  }, [isErrorUpload]);

  useEffect(() => {
    if (isSuccessUpload) {
      notify({
        variant: 'success',
        message: 'Files Added to Patient Charts Successfully',
      });
      faxEventChannel.emit('onUpdateFaxDetails', id);
      onClose?.();
    }
  }, [isSuccessUpload]);

  return (
    <Dialog
      open={open}
      title="Add to Patient Chart"
      onClose={onClose}
      onConfirm={() => {}}
      confirmText="Send"
      dialogClassName="large-width"
      className="w-1/2"
    >
      <Controller
        control={control}
        name="patient"
        rules={{ required: true }}
        render={({ field }) => (
          <PatientSelect
            {...field}
            id="patient-lookup"
            placeholder="Enter patient name, phone or date of birth"
            allowDefault
            required
          />
        )}
      />
      <Controller
        control={control}
        name="fileType"
        rules={{ required: true }}
        render={({ field }) => (
          <Wrapper
            id="media-type-wrapper"
            showErrorText={false}
            label="Media Type"
            required
            labelClasses="font-semibold text-xs text-gray-500"
          >
            <FileTypeSelect
              {...field}
              className="!w-full"
            />
          </Wrapper>
        )}
      />
      <div className="flex flex-row gap-2 justify-end">
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
        <Button
          className="!min-w-[100px]"
          variant="contained"
          color="primary"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </div>
    </Dialog>
  );
}
